import React, { useState, useEffect, useRef, useCallback } from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { bindActionCreators } from "redux";

import { AddCircle as AddCircleIcon, Delete, Edit as EditIcon } from "@material-ui/icons";
import Box from "@material-ui/core/Box";
import { makeStyles } from "@material-ui/core/styles";
import { useSnackbar } from "notistack";
import {
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Link,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Grid,
  Typography,
  Tooltip,
  Checkbox,
  TableSortLabel,
} from "@material-ui/core";
import ImageDefaultProduct from "../../assets/img/default/onboarding_products.jpg";

import ViewDataEmpty from "../../components/ViewDataEmpty/ViewDataEmpty";
import FilterProductsSelect from "../../components/FilterProductsSelect/FilterProductsSelect";
import { getCollectionsService } from "../../services/collections";
import {
  buildCollectionsById,
  getProductCollectionLabels,
} from "../../utils/product-collections-display";

import {
  getProductsService,
  updateProductService,
  getDownloadProducts,
  uploadBatchProductImages,
  deleteBulkProduct,
  bulkEnableProducts,
  bulkDisableProducts,
} from "../../services/products";

import { NameCell, ProductImage, useStyles } from "./ProductsTablePage.styles";

import IconMasive from "../../assets/icons/upload-masive.svg";
import SearchIcon from "../../assets/icons/SearchIcon.svg";
import ExportIcon from "../../assets/icons/ExportIcon.svg";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";

import {
  setProductToEdit,
  setPageState,
  setRowsPerPageState,
} from "../../modules/products";
import { routes, formatNumber } from "../../constants.js/misc";
import useDebounce from "../../utils/debounce";
import { Input, InputAdornment } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import { handleImageBase64 } from "../../utils/handle-base64";
import { Mixpanel } from '../../constants.js/mixpanel'
import ConfirmableButton from "../../components/ConfirmableButton";

const TextOnlyTooltip = withStyles({
  tooltip: {
    fontSize: 15,
  },
})(Tooltip);

const LOCAL_STORAGE_KEY = "selectedProductIds";

const LoadingButtonContent = ({ loading, children, classes }) => (
  <span className={classes.buttonLoadingWrap}>
    <span className={loading ? classes.buttonLoadingLabelHidden : undefined}>
      {children}
    </span>
    {loading && (
      <span className={classes.buttonLoadingSpinner}>
        <CircularProgress size={16} color="inherit" />
      </span>
    )}
  </span>
);

const ProductsTablePage = ({
  storeId,
  setProductToEdit,
  initialRowsPerPage,
  initialPage,
  setPageState,
  setRowsPerPageState,
  storeData,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();

  const columns = [
    { id: "name", label: t("ProductsTable.headers.name") },
    { id: "category", label: t("ProductsTable.headers.categories") },
    { id: "subcategory", label: t("ProductsTable.headers.subcategory") },
    { id: "sku", label: t("ProductsTable.headers.sku") },
    {
      id: "status",
      label: t("ProductsTable.headers.availability"),
      align: "center",
      filterable: true,
    },
    {
      id: "price",
      label: t("ProductsTable.headers.price"),
      sortable: true,
      align: "right",
    },
    { id: "edit", label: "", align: "center" },
  ];

  const visibleColumns = columns;

  const getColumnClassName = (columnId) => {
    switch (columnId) {
      case "name":
        return classes.colName;
      case "category":
        return `${classes.colCategory} ${classes.cellTruncate}`;
      case "subcategory":
        return `${classes.colSubcategory} ${classes.cellTruncate}`;
      case "sku":
        return `${classes.colSku} ${classes.cellTruncate}`;
      case "status":
        return classes.colStatus;
      case "price":
        return classes.colPrice;
      case "edit":
        return classes.colEdit;
      default:
        return undefined;
    }
  };

  const classes = useStyles();
  const [gettingData, setGettingData] = useState(true);
  const [page, setPage] = useState(initialPage);
  const [rows, setRows] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [totalRows, setTotalRows] = useState(0);
  const [firstLoad, setFirstLoad] = useState(true);
  const [filter, setFilter] = useState("");
  const [selectedCategorie, setSelectedCategorie] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [priceSortOrder, setPriceSortOrder] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const isAddBlocked =
    typeof window !== 'undefined' &&
    window.sessionStorage.getItem('freemium-block-add-products') === '1';
  const [exporting, setExporting] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  const [bulkLoadingAction, setBulkLoadingAction] = useState(null);
  const [togglingProductId, setTogglingProductId] = useState(null);
  const [collectionsById, setCollectionsById] = useState({});

  const debouncedFilter = useDebounce(filter, 500);

  const hasActiveFilters =
    Boolean(debouncedFilter) ||
    selectedCategorie ||
    selectedSubcategory ||
    selectedStatus !== null ||
    selectedOrder;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setPageState(newPage);
  };

  const handleFilterChange = ({ target }) => setFilter(target.value);

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setPageState(0);
    setRowsPerPage(+event.target.value);
    setRowsPerPageState(+event.target.value);
  };

  const handlePriceSortClick = () => {
    setSelectedOrder(null);
    setPriceSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
  };

  const handleStatusFilterClick = () => {
    setSelectedStatus((prev) => {
      if (prev === null) return 1;
      if (prev === 1) return 0;
      return null;
    });
  };

  const getProductListQueryParams = useCallback(() => {
    const collectionId = selectedSubcategory
      ? selectedSubcategory.id
      : selectedCategorie
      ? selectedCategorie.id
      : null;

    const sortField = priceSortOrder
      ? "price"
      : selectedOrder
      ? "name"
      : null;
    const sortOrder = priceSortOrder || selectedOrder || null;

    return { collectionId, sortField, sortOrder };
  }, [
    selectedSubcategory,
    selectedCategorie,
    priceSortOrder,
    selectedOrder,
  ]);

  const fetchAllFilteredProductIds = useCallback(async () => {
    const { collectionId, sortField, sortOrder } = getProductListQueryParams();
    const pageSize = 100;
    const allIds = [];
    let currentPage = 0;
    let total = Infinity;

    while (allIds.length < total) {
      const res = await getProductsService(
        storeId,
        pageSize,
        currentPage,
        debouncedFilter,
        selectedStatus,
        sortField,
        sortOrder,
        collectionId
      );

      total = +res.headers["x-total-count"] || 0;
      const batch = (res.data || []).map((product) => product.id);

      if (!batch.length) {
        break;
      }

      allIds.push(...batch);
      currentPage += 1;

      if (allIds.length >= total) {
        break;
      }
    }

    return [...new Set(allIds)];
  }, [
    storeId,
    debouncedFilter,
    selectedStatus,
    getProductListQueryParams,
  ]);

  const fetchProducts = useCallback(async () => {
    setGettingData(true);
    const { collectionId, sortField, sortOrder } = getProductListQueryParams();

    try {
      const res = await getProductsService(
        storeId,
        rowsPerPage,
        page,
        debouncedFilter,
        selectedStatus,
        sortField,
        sortOrder,
        collectionId
      );

        Mixpanel.track('busca el producto', {
            "View": 1,
            "Fuente de trafico": document.referrer
        });

      setTotalRows(+res.headers["x-total-count"]);
      setRows(res.data || []);
      setGettingData(false);
      setFirstLoad(false);
    } catch (err) {
      setGettingData(false);
      setFirstLoad(false);
      enqueueSnackbar(t("ProductsTable.error"), {
        variant: "error",
        autoHideDuration: 2500,
      });
      console.error(err);
    }
  }, [
    storeId,
    rowsPerPage,
    page,
    debouncedFilter,
    selectedStatus,
    selectedOrder,
    priceSortOrder,
    getProductListQueryParams,
    enqueueSnackbar,
    t,
  ]);

  const toggleProduct = (selectedProduct) => {
    setTogglingProductId(selectedProduct.id);
    const newActiveStatus = selectedProduct.active ? 0 : 1;
    updateProductService(
      storeId,
      {
        active: newActiveStatus,
      },
      selectedProduct.id,
      true
    )
      .then(() => {
        setTogglingProductId(null);
        setRows(
          rows.map((product) => ({
            ...product,
            active:
              selectedProduct.id === product.id
                ? newActiveStatus
                : product.active,
          }))
        );
        enqueueSnackbar(
          newActiveStatus
            ? t("ProductsTable.toggleSuccesActive")
            : t("ProductsTable.toggleSuccesInactive"),
          {
            variant: "success",
            autoHideDuration: 2500,
          }
        );
      })
      .catch((err) => {
        setTogglingProductId(null);
        enqueueSnackbar(
          newActiveStatus
            ? t("ProductsTable.toggleErrorActive")
            : t("ProductsTable.toggleErrorInactive"),
          {
            variant: "error",
            autoHideDuration: 2500,
          }
        );
        console.log(err);
      });
  };

  const handleBatchImageExport = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };
  const handleFileSelected = async (event) => {
    const file = event.target?.files?.[0];
    if (!file) return;

    if (!file.type.includes("zip") && !file.name.endsWith(".zip")) {
      enqueueSnackbar(t("ExportImageBatch.ZipFileRequired"), {
        variant: "error",
        autoHideDuration: 2500,
      });
      return;
    }

    setGettingData(true);

    try {
      await uploadBatchProductImages(storeId, file);
      setGettingData(false);
      enqueueSnackbar(t("ExportImageBatch.UploadSuccess"), {
        variant: "success",
        autoHideDuration: 4000,
      });
      await fetchProducts();
    } catch (error) {
      setGettingData(false);
      enqueueSnackbar(t("ExportImageBatch.UploadError"), {
        variant: "error",
        autoHideDuration: 2500,
      });
      console.error(error);
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleExportProduct = async () => {
    try {
      setExporting(true);
      await getDownloadProducts(storeId);
    } finally {
      setExporting(false);
    }
  };

  const getOrderedImages = (images) => {
    return (
      images.sort((a, b) => {
        const aOrder = a.order || 0;
        const bOrder = b.order || 0;
        return aOrder - bOrder;
      })[0]?.picture_url || ""
    );
  };

  const getProductThumbnailSrc = (product) => {
    if (product?.product_images?.length > 0) {
      return `${process.env.REACT_APP_IMAGES_API_URL}${Buffer.from(
        handleImageBase64(getOrderedImages(product.product_images))
      ).toString("base64")}`;
    }

    return `https://ui-avatars.com/api/?name=${encodeURIComponent(
      product?.name || ""
    )}&size=50&background=CAD7CA&color=fff`;
  };

  const saveSelectedProductsToStorage = (ids) => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(ids));
  };

  const getSelectedProductsFromStorage = () => {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  };

  const toggleProductSelection = (productId) => {
    setSelectedProducts((prevSelected) => {
      const updated =
        prevSelected.includes(productId)
          ? prevSelected.filter((id) => id !== productId)
          : [...prevSelected, productId];
      saveSelectedProductsToStorage(updated);
      return updated;
    });
  };

  const selectedCount = selectedProducts.length;
  const useSingularBulkLabel = selectedCount <= 1;

  const handleSelectAllClick = async () => {
    if (selectingAll || gettingData || totalRows === 0) {
      return;
    }

    setSelectingAll(true);

    try {
      const filteredIds = await fetchAllFilteredProductIds();
      const allFilteredSelected =
        filteredIds.length > 0 &&
        filteredIds.every((id) => selectedProducts.includes(id));

      const updated = allFilteredSelected
        ? selectedProducts.filter((id) => !filteredIds.includes(id))
        : [...new Set([...selectedProducts, ...filteredIds])];

      setSelectedProducts(updated);
      saveSelectedProductsToStorage(updated);
    } catch (err) {
      enqueueSnackbar(t("ProductsTable.error"), {
        variant: "error",
        autoHideDuration: 2500,
      });
      console.error(err);
    } finally {
      setSelectingAll(false);
    }
  };

  const handleBulkToggleActive = async (activeValue) => {
    if (!selectedProducts.length) return;

    const idsToUpdate = selectedProducts.filter((id) => {
      const product = rows.find((row) => row.id === id);
      if (!product) return true;
      return !(
        product.is_hidden_by_limit &&
        storeData?.plan_features?.hide_excess_products
      );
    });

    if (!idsToUpdate.length) return;

    const actionKey = activeValue === 1 ? "activate" : "deactivate";
    setBulkLoadingAction(actionKey);
    try {
      if (activeValue === 1) {
        await bulkEnableProducts(storeId, idsToUpdate);
      } else {
        await bulkDisableProducts(storeId, idsToUpdate);
      }

      setRows((currentRows) =>
        currentRows.map((product) =>
          idsToUpdate.includes(product.id)
            ? { ...product, active: activeValue }
            : product
        )
      );

      enqueueSnackbar(
        activeValue
          ? t("ProductsTable.bulkActivateSuccess")
          : t("ProductsTable.bulkDeactivateSuccess"),
        { variant: "success", autoHideDuration: 2500 }
      );
    } catch (err) {
      enqueueSnackbar(
        activeValue
          ? t("ProductsTable.bulkActivateError")
          : t("ProductsTable.bulkDeactivateError"),
        { variant: "error", autoHideDuration: 2500 }
      );
      console.error(err);
    } finally {
      setBulkLoadingAction(null);
    }
  };

  const handleBulkDelete = async () => {
    setBulkLoadingAction("delete");
    try {
      await deleteBulkProduct(storeId, selectedProducts).then(() => {
      enqueueSnackbar("Eliminación exitosa", {
        variant: "success",
        autoHideDuration: 2500,
      });
      window.location.reload();
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      setSelectedProducts([]);
      });
    } catch (e) {
      console.error(e);
    } finally {
      setBulkLoadingAction(null);
    }
  };

  useEffect(() => {
    setPage(0);
    setPageState(0);
  }, [
    debouncedFilter,
    selectedStatus,
    selectedOrder,
    priceSortOrder,
    selectedCategorie,
    selectedSubcategory,
    setPageState,
  ]);

  useEffect(() => {
    if (selectedOrder) {
      setPriceSortOrder(null);
    }
  }, [selectedOrder]);

  useEffect(() => {
    setGettingData(true);
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    getCollectionsService(storeId)
      .then(({ data }) => {
        setCollectionsById(buildCollectionsById(data));
      })
      .catch(() => setCollectionsById({}));
  }, [storeId]);

  useEffect(() => {
    setSelectedProducts(getSelectedProductsFromStorage());
  }, []);

  useEffect(() => {
        Mixpanel.track('Visita la página de productos', {
            "View": 1,
            "Fuente de trafico": document.referrer
        });

        if (window.location.search?.split('source=')?.[1]) {
            let d = new Date();
            d.setTime(d.getTime() + (7 * 24 * 60 * 60 * 1000));
            let expires = "expires=" + d.toUTCString();
            document.cookie = 'source=' + window.location.search.split('source=')[1] + ";" + expires;
        }
    }, []);

  return (
    <>
      {gettingData && firstLoad && (
        <Box
          style={{ height: "100%" }}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress />
        </Box>
      )}
      {rows.length === 0 &&
      !gettingData &&
      !selectedCategorie &&
      selectedStatus !== 0 &&
      !debouncedFilter ? (
        <>
          <ViewDataEmpty
            title="Agregar y gestionar tus productos"
            subtitle="En tu Ecometri puedes subir productos hasta 20 productos en el plan gratuito, y productos ilimitados en el plan Pro. Para crear un catálogo completo de WhatsApp, sube tus productos y agrégalos a tus categorías para que puedas organizarlos correctamente."
            btnTitle={t("ProductsTable.addNew")}
            action={
              isAddBlocked
                ? undefined
                : routes.createProduct
            }
            image={ImageDefaultProduct}
          ></ViewDataEmpty>
        </>
      ) : (
        <>
          {rows.length >= 0 && !firstLoad && (
            <>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  flexDirection: "column",
                  paddingTop: "25px",
                  maxWidth: "100%",
                  overflow: "hidden",
                  boxSizing: "border-box",
                }}
              >
                <div
                  style={{
                    marginRight: "20px",
                    marginBottom: "20px",
                  }}
                >
                  <Input
                    className={classes.filterInput}
                    disableUnderline
                    color="primary"
                    margin="normal"
                    placeholder={t("Product.placeholderFilter")}
                    onChange={handleFilterChange}
                    value={filter}
                    variant="outlined"
                    startAdornment={
                      <InputAdornment position="start">
                        <img src={SearchIcon} alt="Search" />
                      </InputAdornment>
                    }
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-start",
                    flexWrap: isMobile ? "wrap" : "nowrap",
                    gap: isMobile ? "8px" : "10px",
                    marginBottom: "5px",
                  }}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleFileSelected}
                    accept=".zip"
                  />
                  <TextOnlyTooltip
                    arrow
                    title={t("ExportImageBatch.Tooltip")}
                    placement="top"
                  >
                    <Button
                      className={`${classes.actionButton} ${classes.thinButton}`}
                      style={{ marginRight: isMobile ? "0" : "8px" }}
                      size="small"
                      startIcon={<img src={ExportIcon} alt="" />}
                      fullWidth={isMobile}
                      onClick={handleBatchImageExport}
                    >
                      {t("ExportImageBatch.BatchImageExport")}
                    </Button>
                  </TextOnlyTooltip>
                <Button
                    className={`${classes.actionButton} ${classes.thinButton}`}
                    style={{ marginRight: isMobile ? "0" : "8px" }}
                    size="small"
                    startIcon={
                      exporting ? null : (
                        <img src={ExportIcon} alt="" />
                      )
                    }
                    fullWidth={isMobile}
                    onClick={handleExportProduct}
                    disabled={exporting}
                  >
                    <LoadingButtonContent loading={exporting} classes={classes}>
                      {t("ExportProducts.Title")}
                    </LoadingButtonContent>
                  </Button>
                  <Button
                    className={`${classes.actionButton} ${classes.thinButton}`}
                    style={{ marginRight: isMobile ? "0" : "8px" }}
                    size="small"
                    component={RouterLink}
                    startIcon={<img src={IconMasive} alt="" />}
                    fullWidth={isMobile}
                    to={routes.importProducts}
                    disabled={!storeData?.plan_features?.is_bulk_upload_enabled}
                  >
                    {t("ImportProducts.Title")}
                  </Button>
                  <Button
                    className={`${classes.actionButton} ${classes.thinButton}`}
                    color="primary"
                    variant="contained"
                    fullWidth={isMobile}
                    startIcon={<AddCircleIcon />}
                    onClick={() => {
                      if (
                        typeof window !== 'undefined' &&
                        window.sessionStorage.getItem('freemium-block-add-products') === '1'
                      ) {
                        enqueueSnackbar(
                          'Tu plan Freemium permite hasta 20 productos. Mejora tu plan para agregar más.',
                          {
                            variant: 'info',
                            autoHideDuration: 4000,
                          }
                        );
                        return;
                      }
                      history.push(routes.createProduct);
                    }}
                  >
                    {t("ProductsTable.addNew")}
                  </Button>
                </div>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  component="p"
                  style={{ margin: "0 0 12px 0", maxWidth: 720, lineHeight: 1.45 }}
                >
                  {t("ExportImageBatch.AcceptedHint")}
                </Typography>
                <Grid
                  item
                  container
                  xs={12}
                  style={{
                    marginBottom: "20px",
                    justifyContent: "flex-start",
                    display: "flex",
                  }}
                >
                  <FilterProductsSelect
                    setSelectedOrder={setSelectedOrder}
                    selectedOrder={selectedOrder}
                    selectedStatus={selectedStatus}
                    setSelectedStatus={setSelectedStatus}
                    selectedCategorie={selectedCategorie}
                    setSelectedCategorie={setSelectedCategorie}
                    selectedSubcategory={selectedSubcategory}
                    setSelectedSubcategory={setSelectedSubcategory}
                    onClearFilters={() => {
                      setFilter("");
                      setPriceSortOrder(null);
                    }}
                  />
                  {hasActiveFilters && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      style={{ marginTop: 8, width: "100%" }}
                    >
                      {t("ProductsTable.filteredCount", {
                        shown: rows.length,
                        total: totalRows,
                      })}
                    </Typography>
                  )}
                </Grid>
              </div>
              <div className={classes.tableSection} style={{ paddingBottom: "60px" }}>
                <Paper className={classes.tableCard} elevation={0}>
                {rows.length > 0 && (
                  <div className={classes.bulkActionsBar}>
                    <div className={classes.selectionBarLeft}>
                      <Button
                        variant="outlined"
                        disableElevation
                        onClick={handleSelectAllClick}
                        className={classes.selectAllButton}
                        disabled={gettingData || selectingAll || Boolean(bulkLoadingAction)}
                      >
                        <LoadingButtonContent loading={selectingAll} classes={classes}>
                          {t("ProductsTable.selectAll")}
                        </LoadingButtonContent>
                      </Button>
                      {selectedCount > 0 && (
                        <Typography
                          variant="body2"
                          component="span"
                          className={classes.bulkSelectionText}
                        >
                          {t(
                            selectedCount === 1
                              ? "ProductsTable.bulkSelectionOfOne"
                              : "ProductsTable.bulkSelectionOfMany",
                            {
                              selected: selectedCount,
                              total: totalRows,
                            }
                          )}
                        </Typography>
                      )}
                    </div>
                    <div className={classes.bulkActionsButtons}>
                      <Button
                        className={`${classes.bulkButtonBase} ${classes.bulkActivateButton}`}
                        variant="contained"
                        disableElevation
                        onClick={() => handleBulkToggleActive(1)}
                        disabled={
                          gettingData ||
                          selectedProducts.length === 0 ||
                          selectingAll ||
                          Boolean(bulkLoadingAction)
                        }
                      >
                        <LoadingButtonContent
                          loading={bulkLoadingAction === "activate"}
                          classes={classes}
                        >
                          {t(
                            useSingularBulkLabel
                              ? "ProductsTable.activateSelectedOne"
                              : "ProductsTable.activateSelectedMany"
                          )}
                        </LoadingButtonContent>
                      </Button>
                      <Button
                        className={`${classes.bulkButtonBase} ${classes.bulkDeactivateButton}`}
                        variant="outlined"
                        disableElevation
                        onClick={() => handleBulkToggleActive(0)}
                        disabled={
                          gettingData ||
                          selectedProducts.length === 0 ||
                          selectingAll ||
                          Boolean(bulkLoadingAction)
                        }
                      >
                        <LoadingButtonContent
                          loading={bulkLoadingAction === "deactivate"}
                          classes={classes}
                        >
                          {t(
                            useSingularBulkLabel
                              ? "ProductsTable.deactivateSelectedOne"
                              : "ProductsTable.deactivateSelectedMany"
                          )}
                        </LoadingButtonContent>
                      </Button>
                      <ConfirmableButton
                        className={`${classes.bulkButtonBase} ${classes.bulkDeleteButton}`}
                        variant="contained"
                        disableElevation
                        startIcon={bulkLoadingAction === "delete" ? null : <Delete />}
                        onClick={handleBulkDelete}
                        disabled={
                          gettingData ||
                          selectedProducts.length === 0 ||
                          selectingAll ||
                          Boolean(bulkLoadingAction)
                        }
                        confirmMessage={
                          useSingularBulkLabel
                            ? t("ProductsTable.deleteConfirmOne")
                            : t("ProductsTable.deleteConfirmMany", {
                                count: selectedCount,
                              })
                        }
                        confirmButtonText="Eliminar"
                        cancelButtonText="Cancelar"
                      >
                        <LoadingButtonContent
                          loading={bulkLoadingAction === "delete"}
                          classes={classes}
                        >
                          {t(
                            useSingularBulkLabel
                              ? "ProductsTable.deleteSelectedOne"
                              : "ProductsTable.deleteSelectedMany"
                          )}
                        </LoadingButtonContent>
                      </ConfirmableButton>
                    </div>
                  </div>
                )}
                  <TableContainer className={classes.container}>
                    <div style={{ minHeight: "4px" }}>
                      {gettingData && <LinearProgress />}
                    </div>
                    <Table stickyHeader aria-label="sticky table" className={classes.table}>
                      <TableHead>
                        <TableRow>
                          {visibleColumns.map((column) => (
                            <TableCell
                              key={column.id}
                              align={column.align}
                              className={getColumnClassName(column.id)}
                            >
                              {column.id === "name" ? (
                                <div className={classes.nameCellInner}>
                                  <Checkbox
                                    className={classes.listHeaderCheckbox}
                                    disabled
                                    checked={false}
                                    tabIndex={-1}
                                    aria-hidden="true"
                                  />
                                  <span
                                    className={classes.nameHeaderImageSpacer}
                                    aria-hidden="true"
                                  />
                                  {column.label}
                                </div>
                              ) : column.filterable ? (
                                <TableSortLabel
                                  active={selectedStatus !== null}
                                  direction={selectedStatus === 1 ? "asc" : "desc"}
                                  onClick={handleStatusFilterClick}
                                  className={`${classes.priceSortLabel} ${
                                    selectedStatus !== null ? classes.priceSortLabelActive : ""
                                  }`}
                                >
                                  {column.label}
                                </TableSortLabel>
                              ) : column.sortable ? (
                                <TableSortLabel
                                  active
                                  direction={priceSortOrder || "desc"}
                                  onClick={handlePriceSortClick}
                                  className={`${classes.priceSortLabel} ${
                                    priceSortOrder ? classes.priceSortLabelActive : ""
                                  }`}
                                >
                                  {column.label}
                                </TableSortLabel>
                              ) : (
                                column.label
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((row, index) => {
                          const isHiddenByLimit =
                            row.is_hidden_by_limit &&
                            storeData?.plan_features?.hide_excess_products;
                          const { categories, subcategories } =
                            getProductCollectionLabels(row, collectionsById);

                          return (
                            <TableRow
                              hover
                              role="checkbox"
                              tabIndex={-1}
                              key={row.name + index}
                              className={
                                selectedProducts.includes(row.id)
                                  ? classes.rowSelected
                                  : undefined
                              }
                            >
                              <NameCell className={`${classes.colName} ${classes.nameTableCell}`}>
                                <div className={classes.nameCellInner}>
                                  <Checkbox
                                    className={classes.rowCheckbox}
                                    checked={selectedProducts.includes(row.id)}
                                    onChange={() => toggleProductSelection(row.id)}
                                  />
                                  <ProductImage
                                    src={getProductThumbnailSrc(row)}
                                    alt={row.name}
                                    isLast={index === rows.length - 1}
                                  />
                                  <div className={classes.nameCellText}>
                                    <Link
                                      onClick={() => setProductToEdit(row)}
                                      component={RouterLink}
                                      to={"/products/update/" + row.id}
                                    >
                                      {row.name}
                                    </Link>
                                    {isHiddenByLimit && (
                                      <span
                                        style={{
                                          fontSize: 12,
                                          color: "#f44336",
                                          marginTop: 4,
                                        }}
                                      >
                                        Este producto está bloqueado por el límite de tu plan. Solo
                                        se podrán visualizar y eliminar.
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </NameCell>
                              <TableCell className={classes.colCategory}>
                                {categories ? (
                                  <span
                                    className={classes.collectionBadge}
                                    title={categories}
                                  >
                                    {categories}
                                  </span>
                                ) : (
                                  <span className={classes.emptyCell}>—</span>
                                )}
                              </TableCell>
                              <TableCell className={classes.colSubcategory}>
                                {subcategories ? (
                                  <span
                                    className={classes.subcategoryBadge}
                                    title={subcategories}
                                  >
                                    {subcategories}
                                  </span>
                                ) : (
                                  <span className={classes.emptyCell}>—</span>
                                )}
                              </TableCell>
                            <TableCell className={`${classes.colSku} ${classes.skuText}`}>
                              <span className={classes.cellTruncate} title={row.sku}>
                                {row.sku}
                              </span>
                            </TableCell>
                            <TableCell className={classes.colStatus}>
                              <Tooltip
                                title={
                                  row.active
                                    ? t("ProductsTable.deactivateProduct")
                                    : t("ProductsTable.activateProduct")
                                }
                              >
                                <span className={classes.statusSwitchWrap}>
                                  <Switch
                                    disabled={
                                      isHiddenByLimit || togglingProductId === row.id
                                    }
                                    checked={Boolean(row.active)}
                                    onChange={() => toggleProduct(row)}
                                    color="primary"
                                    size="small"
                                    style={
                                      togglingProductId === row.id
                                        ? { opacity: 0 }
                                        : undefined
                                    }
                                    inputProps={{
                                      "aria-label": row.active
                                        ? t("ProductsTable.deactivateProduct")
                                        : t("ProductsTable.activateProduct"),
                                    }}
                                  />
                                  {togglingProductId === row.id && (
                                    <CircularProgress
                                      size={14}
                                      className={classes.statusSwitchSpinner}
                                    />
                                  )}
                                </span>
                              </Tooltip>
                            </TableCell>
                            <TableCell className={`${classes.colPrice} ${classes.priceText}`}>
                              {formatNumber(row.price, storeData.currency)}
                            </TableCell>
                            <TableCell className={classes.colEdit}>
                              <Tooltip title={t("ProductsTable.headers.edit")}>
                                <span>
                                  <IconButton
                                    size="small"
                                    className={classes.editButton}
                                    onClick={() => setProductToEdit(row)}
                                    component={RouterLink}
                                    to={"/products/update/" + row.id}
                                    disabled={isHiddenByLimit}
                                    aria-label={t("ProductsTable.headers.edit")}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    shape="rounded"
                    variant="outlined"
                    labelRowsPerPage={t("table.rowsPerPageLabel")}
                    labelDisplayedRows={({ from, to, count }) =>
                      `${t("table.toShow")} ${to} ${t("table.of")} ${count} ${t(
                        "table.products"
                      )}`
                    }
                    rowsPerPageOptions={totalRows > 0 ? [10, 25, 50] : []}
                    component="div"
                    className={`${classes.spacer} ${classes.tablePagination}`}
                    count={totalRows}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                  />
                </Paper>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
};

const mapStateToProps = ({ auth, products, common }) => ({
  isLoggingIn: auth.isLoggingIn,
  signUpData: auth.signUpData,
  storeId: auth.storeId,
  initialRowsPerPage: products.rowsPerPage,
  initialPage: products.page,
  storeData: common.storeData,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      setProductToEdit,
      setPageState,
      setRowsPerPageState,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(ProductsTablePage);
